import { useCallback, useEffect, useState } from 'react';
import {
  OFFLINE_ACTION_QUEUE_KEY,
  areOfflineQueuesEqual,
  readOfflineActionQueue
} from './offlineQueueUtils';

function useOfflineQueue({ api, loadData, setError, setSuccess, setIsOnline }) {
  const [offlineSyncing, setOfflineSyncing] = useState(false);
  const [offlineQueueOpen, setOfflineQueueOpen] = useState(false);
  const [offlineQueueSnapshot, setOfflineQueueSnapshot] = useState(() => readOfflineActionQueue());

  const setOfflineActionQueue = useCallback((queue) => {
    const safeQueue = Array.isArray(queue) ? queue : [];
    localStorage.setItem(OFFLINE_ACTION_QUEUE_KEY, JSON.stringify(safeQueue));
    setOfflineQueueSnapshot(safeQueue);
  }, []);

  const enqueueOfflineAction = useCallback((type, payload) => {
    const queue = readOfflineActionQueue();
    queue.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      queued_at: new Date().toISOString(),
      type,
      payload
    });
    setOfflineActionQueue(queue);
  }, [setOfflineActionQueue]);

  const flushOfflineActionQueue = useCallback(async () => {
    if (!navigator.onLine || offlineSyncing) return;
    const queue = readOfflineActionQueue();
    if (queue.length === 0) return;
    setOfflineSyncing(true);
    let synced = 0;
    let remaining = [];
    for (let i = 0; i < queue.length; i += 1) {
      const entry = queue[i];
      try {
        if (entry.type === 'order_create') {
          await api.createOrder(entry.payload);
        } else if (entry.type === 'transaction_create') {
          await api.addTransaction(entry.payload);
        } else if (entry.type === 'product_create') {
          await api.createProduct(entry.payload);
        } else if (entry.type === 'order_note_update') {
          await api.updateOrderNotes(entry.payload.id, entry.payload.notes);
        } else if (entry.type === 'product_note_update') {
          await api.updateProductNotes(entry.payload.id, entry.payload.notes);
        } else {
          throw new Error(`Unsupported offline action: ${entry.type}`);
        }
        synced += 1;
      } catch (err) {
        remaining = queue.slice(i);
        setError(`Failed to sync offline actions. ${err.message}`);
        break;
      }
    }
    if (remaining.length === 0) {
      setOfflineActionQueue([]);
    } else {
      setOfflineActionQueue(remaining);
    }
    if (synced > 0) {
      setSuccess(`Synced ${synced} offline action${synced > 1 ? 's' : ''}.`);
      await loadData({ silent: true });
    }
    setOfflineSyncing(false);
  }, [api, loadData, offlineSyncing, setError, setOfflineActionQueue, setSuccess]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      flushOfflineActionQueue();
    };
    const handleOffline = () => setIsOnline(false);
    const handleStorage = (event) => {
      if (event.key !== OFFLINE_ACTION_QUEUE_KEY) return;
      const nextQueue = readOfflineActionQueue();
      setOfflineQueueSnapshot((prev) => (areOfflineQueuesEqual(prev, nextQueue) ? prev : nextQueue));
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('storage', handleStorage);
    if (navigator.onLine) {
      flushOfflineActionQueue();
    }
    const nextQueue = readOfflineActionQueue();
    setOfflineQueueSnapshot((prev) => (areOfflineQueuesEqual(prev, nextQueue) ? prev : nextQueue));
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('storage', handleStorage);
    };
  }, [flushOfflineActionQueue, setIsOnline]);

  return {
    enqueueOfflineAction,
    flushOfflineActionQueue,
    offlineQueueOpen,
    offlineQueueSnapshot,
    offlineSyncing,
    setOfflineActionQueue,
    setOfflineQueueOpen
  };
}

export default useOfflineQueue;
